import { createContext, useContext } from 'react'
import { Navigate } from 'react-router-dom'
import { useLogout, useViewer } from '~/features/auth/api'
import api, { AuthUserType } from '../api'
import Forbidden from './Forbidden'
import ForbiddenApes from './ForbiddenApes'
import ForbiddenDroids from './ForbiddenDroids'
import ForbiddenGeneric from './ForbiddenGeneric'
import { canUsePacks } from './navigation'

type AuthContextType = {
    auth?: AuthUserType
    setAuth?
    logout: () => void
    checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
    logout: api.account.logout,
    checkAuth: async () => {},
})

export const AuthProvider = ({ children }) => {
    const viewer = useViewer()
    const logout = useLogout()

    return (
        <AuthContext.Provider
            value={{
                auth: viewer.data as unknown as AuthUserType,
                setAuth: () => undefined,
                logout: logout.mutate,
                checkAuth: () => undefined,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const ctx = useContext(AuthContext)
    if (!ctx) {
        throw new Error('useBanner must be used within a BannerProvider')
    }
    return ctx
}

export const RequireAuth = (props) => {
    const viewer = useViewer()
    const children = props.children
    const fallback = props.fallback || '/login'

    if (viewer.isLoading) return null
    if (viewer.data?.is_auth === false) return <Navigate to={fallback} replace={true} />
    if (viewer.data?.is_auth === true) {
        if (viewer.data?.email_confirmed == true) {
            return children
        } else {
            return <Navigate to='/activate' replace={true} />
        }
    }
}

export const IsAuth = ({ children }) => {
    const { auth } = useAuth()
    if (auth) return auth?.is_auth === true ? children : null
}

export const IsHolder = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth)
        return auth?.is_ape_holder === true || auth?.is_droid_holder === true ? (
            children
        ) : show403 ? (
            <Forbidden />
        ) : null
}

export const IsApeHolder = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth) return auth?.is_ape_holder === true ? children : show403 ? <ForbiddenApes /> : null
}

export const IsDroidHolder = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth) return auth?.is_droid_holder === true ? children : show403 ? <ForbiddenDroids /> : null
}

export const IsStaff = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth)
        return [auth.is_staff, auth.is_developer, auth.is_marketing, auth.is_executive].some((e) => e) === true ? (
            children
        ) : show403 ? (
            <ForbiddenGeneric />
        ) : null
}

/**
 * Who may open Team Tools at all: any role flag, or a capability granted in the
 * admin. Only the Team Tools route uses this. `IsStaff` stays flag-only, because
 * it still guards Store, Packs and Skins, which a grant does not reach.
 */
export const IsTeamToolsUser = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth)
        return [auth.is_staff, auth.is_developer, auth.is_marketing, auth.is_executive, auth.has_team_tools_grant].some(
            (e) => e
        ) === true ? (
            children
        ) : show403 ? (
            <ForbiddenGeneric />
        ) : null
}

/** Staff always; players once the Packs kill switch in the Django admin has
 *  "open to players" ticked. The server is the gate and answers 403 on its
 *  own; this only stops a player being shown a page every button of which
 *  would be refused. */
export const CanUsePacks = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth) return canUsePacks(auth) ? children : show403 ? <ForbiddenGeneric /> : null
}

export const IsDeveloper = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth)
        return [auth.is_developer, auth.is_executive].some((e) => e) === true ? (
            children
        ) : show403 ? (
            <ForbiddenGeneric />
        ) : null
}

export const IsMarketing = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth)
        return [auth.is_marketing, auth.is_executive].some((e) => e) === true ? (
            children
        ) : show403 ? (
            <ForbiddenGeneric />
        ) : null
}

/**
 * The affiliate gate.
 *
 * Not folded into the staff wrappers above: those are privilege levels that nest
 * (executive implies marketing implies staff), while being an affiliate is
 * orthogonal to all of them. A marketing staffer is not automatically an
 * affiliate, and an affiliate is emphatically not staff.
 */
export const IsAffiliate = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth) return auth?.is_affiliate === true ? children : show403 ? <ForbiddenGeneric /> : null
}

/**
 * The agency gate.
 *
 * Narrower than `IsAffiliate`, not a sibling of it: every manager is also an
 * affiliate, so checking this first would send plain affiliates to a 403 on a
 * page they should never have seen a link to.
 */
export const IsAffiliateManager = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth) return auth?.is_affiliate_manager === true ? children : show403 ? <ForbiddenGeneric /> : null
}

export const IsExecutive = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth) return auth?.is_executive === true ? children : show403 ? <ForbiddenGeneric /> : null
}

export const IsMarketingOrDeveloper = ({ children, show403 = false }) => {
    const { auth } = useAuth()
    if (auth)
        return [auth.is_marketing, auth.is_developer, auth.is_executive].some((e) => e) === true ? (
            children
        ) : show403 ? (
            <ForbiddenGeneric />
        ) : null
}
